import { useEffect, useRef, useState } from "react";
import { apiCall } from "../lib/api";

export type TypeaheadOrder = {
  invoiceId?: string;
  date?: string;
  twinChecks?: string;
  rollTypes?: string;
  rollCount?: string | number;
  returnOption?: string;
  returnStatus?: string;
  prints?: string;
  notes?: string;
  isProcessed?: boolean;
};
export type Suggestion = {
  type: "customer" | "twin-check" | string;
  value: string;
  customerId?: string;
  orderCount?: number;
  orders?: TypeaheadOrder[];
};

function orderCardStyle(order: TypeaheadOrder): { statusClass: string; borderColor: string } {
  const returnStatus = (order.returnStatus || "").toLowerCase();
  const isNoShow = returnStatus === "no show - tossed";
  const isCompleted =
    returnStatus.includes("picked") || returnStatus.includes("tossed") || returnStatus.includes("mailed");
  let statusClass = "";
  let borderColor = "#D5A81C";
  if (isNoShow) {
    statusClass = "status-no-show";
    borderColor = "#9b59b6";
  } else if (isCompleted) {
    statusClass = "status-complete";
    borderColor = "#4CAF50";
  } else if (order.returnStatus) {
    statusClass = "status-processing";
    borderColor = "#2196F3";
  }
  if (!order.isProcessed) {
    borderColor = "#FFA500";
    statusClass = "status-processing";
  }
  return { statusClass, borderColor };
}

/** Debounced /api/typeahead. Min 2 chars, 200ms debounce (matches handleTypeahead). */
export function useTypeahead(query: string) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const data = await apiCall<{ suggestions: Suggestion[] }>(`typeahead?q=${encodeURIComponent(q)}`);
        setSuggestions(data.suggestions || []);
      } catch (e) {
        console.error("Typeahead error:", e);
      }
    }, 200);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);
  return suggestions;
}

export function TypeaheadResults({
  suggestions,
  onSelectCustomer,
  onSelectValue,
  onViewOrder,
}: {
  suggestions: Suggestion[];
  onSelectCustomer: (customerId: string, value: string) => void;
  onSelectValue: (value: string) => void;
  onViewOrder: (invoiceId: string) => void;
}) {
  if (!suggestions.length) return null;
  return (
    <>
      {suggestions.map((s, i) => {
        if (s.type === "customer") {
          const orderText = s.orderCount === 1 ? "order" : "orders";
          return (
            <div className="typeahead-item customer-expanded" key={i}>
              <div
                className="typeahead-customer-header-expanded"
                onClick={() => (s.customerId ? onSelectCustomer(s.customerId, s.value) : onSelectValue(s.value))}
              >
                <h3 className="customer-name">{s.value}</h3>
                <span className="order-count">
                  {s.orderCount} {orderText}
                </span>
              </div>
              {s.orders && s.orders.length > 0 && (
                <div className="typeahead-orders-expanded">
                  {s.orders.map((order, j) => {
                    const { statusClass, borderColor } = orderCardStyle(order);
                    return (
                      <div
                        className={`typeahead-order-card ${statusClass}`}
                        style={{ borderLeft: `5px solid ${borderColor}` }}
                        key={j}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (order.invoiceId) onViewOrder(order.invoiceId);
                        }}
                      >
                        <div className="typeahead-order-header">
                          <span className="order-date">{order.date}</span>
                          <span className="twin-check-badge">{order.twinChecks || "No Twin Check"}</span>
                        </div>
                        <div className="typeahead-order-details">
                          <div className="order-detail-row">
                            <span className="detail-label">Roll Types:</span>
                            <span className="detail-value">{order.rollTypes || "N/A"}</span>
                          </div>
                          <div className="order-detail-row">
                            <span className="detail-label">Amount:</span>
                            <span className="detail-value">{order.rollCount || "0"} rolls</span>
                          </div>
                          <div className="order-detail-row">
                            <span className="detail-label">Return Option:</span>
                            <span className="detail-value">{order.returnOption || "N/A"}</span>
                          </div>
                          <div className="order-detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                              {order.returnStatus || (order.isProcessed ? "Completed" : "Processing")}
                            </span>
                          </div>
                          <div className="order-detail-row">
                            <span className="detail-label">Prints:</span>
                            <span className={`print-indicator ${order.prints ? "has-prints" : "no-prints"}`}>
                              {order.prints || "None"}
                            </span>
                          </div>
                          {order.notes && (
                            <div className="order-detail-row full-width">
                              <span className="detail-label">Notes:</span>
                              <span className="detail-value">{order.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="typeahead-item twin-check" key={i} onClick={() => onSelectValue(s.value)}>
            <i className="ph ph-ticket twin-check-icon" />
            {s.value}
          </div>
        );
      })}
    </>
  );
}
